import { useEffect, useMemo } from 'react';

import { excludeDescendants } from '@gasbi/shared/common/features/subdivisions/tree';
import { buildGridifyQuery } from '@gasbi/shared/web/components/Filter';
import {
  makeFilterItemPrimitive,
  makeFilterItemTextSearch,
} from '@gasbi/shared/web/components/Filter/buildGridifyQuery/utils';

import { useGetSubdivisionsInfiniteQuery } from '@/features/subdivisions/subdivisionsApi';

/**
 * The active підрозділи of the ГРМ that may become the parent of `subdivisionId`: neither the subdivision itself nor
 * any of its descendants (GSB-8373), as the shared SubdivisionForm picks them.
 */
export const useParentSubdivisionOptions = (
  orgId?: string,
  subdivisionId?: string,
  currentParentId?: string | null,
) => {
  const activeQuery = useMemo(
    () =>
      buildGridifyQuery([
        makeFilterItemPrimitive('isActive', true),
        makeFilterItemPrimitive('headOrganizationId', orgId),
        makeFilterItemTextSearch('name', undefined),
      ])
        .addOrderBy('name')
        .build(),
    [orgId],
  );

  // Every subdivision of the ГРМ, active or not: the descendant graph must not break at an inactive intermediate.
  const allQuery = useMemo(
    () =>
      buildGridifyQuery([
        makeFilterItemPrimitive('headOrganizationId', orgId),
        makeFilterItemTextSearch('name', undefined),
      ])
        .addOrderBy('name')
        .build(),
    [orgId],
  );

  const {
    data: active = [],
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetSubdivisionsInfiniteQuery(activeQuery, { skip: !orgId });

  const {
    data: all = [],
    hasNextPage: hasMoreAll,
    isLoading: isLoadingAll,
    isFetching: isFetchingAll,
    isError: isAllError,
    fetchNextPage: fetchMoreAll,
  } = useGetSubdivisionsInfiniteQuery(allQuery, {
    skip: !orgId || !subdivisionId,
  });

  // Descendants are only known once every page of the full list is loaded; stop on a failed page.
  useEffect(() => {
    if (subdivisionId && hasMoreAll && !isFetchingAll && !isAllError) {
      fetchMoreAll();
    }
  }, [subdivisionId, hasMoreAll, isFetchingAll, isAllError, fetchMoreAll]);

  // A new підрозділ has no descendants. An edited one offers no parent until all its descendants are known: a partial
  // list would offer one, and the server refuses the cycle on save.
  const isWaitingForAll =
    !!subdivisionId &&
    !isAllError &&
    (isLoadingAll || isFetchingAll || !!hasMoreAll);

  // Until then only the current parent stays, which the server already holds as valid.
  const options = useMemo(
    () =>
      subdivisionId && (isWaitingForAll || isAllError)
        ? active.filter(s => !!currentParentId && s.id === currentParentId)
        : excludeDescendants(active, subdivisionId, all),
    [active, subdivisionId, all, isWaitingForAll, isAllError, currentParentId],
  );

  return {
    options,
    isLoading: isLoading || isWaitingForAll,
    // Kept apart from loading: a failed list is not a list still on its way.
    isError: !!subdivisionId && isAllError,
    hasNextPage,
    fetchNextPage,
  };
};
